import { ReactNode } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ChangeLine from '../../../components/novarium/ChangeLine'
import InfoTip from '../../../components/novarium/InfoTip'
import { OperatingRoomSummaryDTO } from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { percentVariation, pointsVariation, Variation } from '../../ServicesStatistics/statisticsChange'
import { OperatingRoomDrill } from '../operatingRoomFilterModel'
import { formatCount, formatDuration, formatHours, formatHoursPlain, formatPercent } from '../operatingRoomFormat'
import { previousPeriodRange, toRatio } from '../operatingRoomVariation'
import Indicator from './OperatingRoomIndicatorCard'

/** «Con 4 cirugías sin horario válido: …»: una frase más de la ⓘ, solo si hay (N > 0). */
function noteIf(intl: IntlShape, id: string, count: number): string {
  return count > 0 ? ` ${intl.formatMessage({ id }, { count, countText: formatCount(intl, count) })}` : ''
}

interface OperatingRoomIndicatorsProps {
  summary: OperatingRoomSummaryDTO
  /** Un número abre la lista con las cirugías que cuenta. */
  onDrill: (drill: OperatingRoomDrill) => void
}

/**
 * Los indicadores del período, cada uno con su variación contra el período anterior de igual largo, escrita con las fechas de ese
 * período. Todos son neutros, sin «mejor» ni «peor»: la dirección médica todavía no validó qué es mejor en ninguno. La fórmula de
 * cada uno está a demanda en su ⓘ, y lo que no cierra en los datos (cirugías sin horario válido, superpuestas, salas sin agenda)
 * se suma a la ⓘ del indicador al que afecta, solo si hay. Programadas (con agenda) y Fuera de agenda son independientes de
 * Urgentes. Anuladas, reprogramadas y ausentes son tres cuentas, nunca una suma «canceladas». Los números de las cirugías abren
 * la lista.
 */
export default function OperatingRoomIndicators({ summary, onDrill }: OperatingRoomIndicatorsProps) {
  const intl = useIntl()
  const { current, previous, dataNotes } = summary
  const period = previousPeriodRange(summary.previousFrom, summary.previousTo)

  const line = (variation: Variation | null): ReactNode =>
    (variation && period ? <ChangeLine variation={variation} period={period} /> : undefined)

  const text = (id: string): string => intl.formatMessage({ id })
  const filteredHint = summary.occupancyOfFilteredSurgeries ? ` ${text('OperatingRoomIndicatorsKpiOccupancyFilteredHint')}` : ''

  const share = (pct: number | null, id: string): ReactNode =>
    (pct === null ? undefined : intl.formatMessage({ id }, { pct: formatPercent(intl, pct) }))

  return (
    <div className="nv-or-indicators-box">
      <div className="nv-or-indicators" role="group" aria-label={text('OperatingRoomIndicatorsKpisLabel')}>
        <Indicator
          label={text('OperatingRoomIndicatorsKpiSurgeries')}
          hint={text('OperatingRoomIndicatorsKpiSurgeriesHint')}
          value={formatCount(intl, current.surgeries)}
          onDrill={() => onDrill({ outcome: 'COMPLETED' })}
          details={[line(percentVariation(current.surgeries, previous.surgeries))]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiScheduled')}
          hint={text('OperatingRoomIndicatorsKpiScheduledHint')}
          value={formatCount(intl, current.scheduled)}
          onDrill={() => onDrill({ outcome: 'COMPLETED', patch: { agenda: 'SCHEDULED' } })}
          details={[share(current.scheduledPct, 'OperatingRoomIndicatorsOfSurgeries'), line(percentVariation(current.scheduled, previous.scheduled))]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiOutOfAgenda')}
          hint={text('OperatingRoomIndicatorsKpiOutOfAgendaHint')}
          value={formatCount(intl, current.outOfAgenda)}
          onDrill={() => onDrill({ outcome: 'COMPLETED', patch: { agenda: 'OUT_OF_AGENDA' } })}
          details={[share(current.outOfAgendaPct, 'OperatingRoomIndicatorsOfSurgeries'), line(percentVariation(current.outOfAgenda, previous.outOfAgenda))]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiUrgent')}
          hint={text('OperatingRoomIndicatorsKpiUrgentHint')}
          value={formatCount(intl, current.urgent)}
          onDrill={() => onDrill({ outcome: 'COMPLETED', patch: { urgency: 'URGENT' } })}
          details={[share(current.urgentPct, 'OperatingRoomIndicatorsOfSurgeries'), line(percentVariation(current.urgent, previous.urgent))]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiHours')}
          hint={text('OperatingRoomIndicatorsKpiHoursHint')
            + noteIf(intl, 'OperatingRoomIndicatorsNoteWithoutTimes', dataNotes.surgeriesWithoutValidTimes)
            + noteIf(intl, 'OperatingRoomIndicatorsNoteTimedByQueue', dataNotes.surgeriesTimedByQueue)
            + noteIf(intl, 'OperatingRoomIndicatorsNoteOverlapping', dataNotes.overlappingSurgeries)}
          value={formatHours(intl, current.hoursUsed)}
          details={[
            current.hoursUsed > 0
              ? intl.formatMessage({ id: 'OperatingRoomIndicatorsOutsideOfHours' }, {
                hours: formatHours(intl, current.outsideBlockHours), pct: formatPercent(intl, current.outsideBlockPct),
              })
              : undefined,
            line(percentVariation(current.hoursUsed, previous.hoursUsed)),
          ]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiOccupancy')}
          hint={text('OperatingRoomIndicatorsKpiOccupancyHint') + filteredHint
            + noteIf(intl, 'OperatingRoomIndicatorsNoteRoomsWithoutTemplate', dataNotes.roomsWithoutTemplate)
            + noteIf(intl, 'OperatingRoomIndicatorsNoteDaysWithoutTemplate', dataNotes.daysWithoutTemplate)}
          value={formatPercent(intl, current.occupancyPct)}
          details={[
            current.capacityHours > 0
              ? intl.formatMessage({ id: 'OperatingRoomIndicatorsOfHours' }, {
                count: formatHoursPlain(intl, current.insideBlockHours),
                total: formatHoursPlain(intl, current.capacityHours),
              })
              : undefined,
            line(pointsVariation(intl, toRatio(current.occupancyPct), toRatio(previous.occupancyPct))),
          ]}
        />
        <Indicator
          label={text('OperatingRoomIndicatorsKpiMedian')}
          hint={text('OperatingRoomIndicatorsKpiMedianHint')}
          value={formatDuration(intl, current.medianDurationMinutes)}
          details={[
            current.durationSample > 0
              ? intl.formatMessage({ id: 'OperatingRoomIndicatorsOfSample' }, { countText: formatCount(intl, current.durationSample) })
              : undefined,
            current.medianDurationMinutes === null || previous.medianDurationMinutes === null
              ? undefined
              : line(percentVariation(current.medianDurationMinutes, previous.medianDurationMinutes)),
          ]}
        />
        <div className="nv-card nv-or-ind nv-or-triple" role="group" aria-label={text('OperatingRoomIndicatorsNotDoneLabel')}>
          {([
            ['OperatingRoomIndicatorsKpiAnnulled', 'OperatingRoomIndicatorsKpiAnnulledHint', current.annulled, previous.annulled, 'ANNULLED'],
            ['OperatingRoomIndicatorsKpiRescheduled', 'OperatingRoomIndicatorsKpiRescheduledHint', current.rescheduled, previous.rescheduled, 'RESCHEDULED'],
            ['OperatingRoomIndicatorsKpiMissed', 'OperatingRoomIndicatorsKpiMissedHint', current.missed, previous.missed, 'MISSED'],
          ] as const).map(([labelId, hintId, now, before, outcome]) => (
            <dl key={outcome} className="nv-or-triple__row">
              <dt className="nv-or-triple__name">
                {text(labelId)}
                <InfoTip text={text(hintId)} />
              </dt>
              <dd className="nv-or-triple__value">
                <button type="button" className="nv-or-drill" onClick={() => onDrill({ outcome })}>{formatCount(intl, now)}</button>
              </dd>
              <dd className="nv-or-triple__change nv-or-ind__detail">{line(percentVariation(now, before))}</dd>
            </dl>
          ))}
        </div>
      </div>
    </div>
  )
}
